import React from "react";
import FAQ from "../../pages/Faq";
import { Helmet } from "react-helmet-async";
import { FaCheckCircle } from "react-icons/fa";
import {
  FaEnvelopeOpenText,
  FaCaretRight,
  FaClipboardList,
  FaCoins,
  FaCalendar,
  FaTable,
  FaImage,
} from "react-icons/fa6";

import { Check } from "lucide-react";
import SalaryIncrementLetterGeneratorForm from "./SalaryIncrementLetterGeneratorForm";
import { FaPercentage } from "react-icons/fa";
import {
  FaClock,
  FaEnvelope,
  FaExclamationTriangle,
  FaMoneyBillWave,
  FaUsers,
  FaUser,
  FaFileAlt,
} from "react-icons/fa";

const companystepsFeatures = [
  {
    id: 1,
    icon: <FaClock />,
    title: "Save HR Time",
    desc: "Generate an Increment Letter without drafting every salary communication manually.",
  },
  {
    id: 2,
    icon: <FaEnvelope />,
    title: "Maintain a Standard Format",
    desc: "Use a consistent Salary Increment Letter Format across departments and employee levels.",
  },
  {
    id: 3,
    icon: <FaExclamationTriangle />,
    title: "Reduce Salary Errors",
    desc: "Structured fields help prevent incorrect CTC, percentage and effective-date information.",
  },
  {
    id: 4,
    icon: <FaMoneyBillWave />,
    title: "Improve Salary Transparency",
    desc: "Clearly communicate the employee's previous and revised compensation.",
  },
  {
    id: 5,
    icon: <FaUsers />,
    title: "Recognise Employee Performance",
    desc: "Use the letter to formally appreciate the employee's contribution and progress.",
  },
  {
    id: 6,
    icon: <FaFileAlt />,
    title: "Maintain Accurate HR Records",
    desc: "Store a clear record of each salary revision in the employee's HR file.",
  },
];

const steps = [
  {
    number: 1,
    title: "Enter Company Details",
    description:
      "Add the company name, address and authorised signatory information.",
  },
  {
    number: 2,
    title: "Add Employee Information",
    description: "Enter the employee's name, ID, designation and department.",
  },
  {
    number: 3,
    title: "Select the Increment Type",
    description:
      "Choose annual appraisal, performance-based increment, market correction, retention increment or another reason.",
  },
  {
    number: 4,
    title: "Enter Current Salary",
    description:
      "Add the employee's existing annual CTC, monthly gross salary and variable pay.",
  },
  {
    number: 5,
    title: "Add Revised Salary",
    description:
      "Enter the revised CTC, monthly salary, increment amount and effective date.",
  },
  {
    number: 6,
    title: "Add Performance Information",
    description:
      "Include a short appreciation statement, key achievements and future expectations.",
  },
  {
    number: 7,
    title: "Generate the Letter",
    description:
      "The system will insert the details into the standard Increment Letter format.",
  },
  {
    number: 8,
    title: "Review and Issue",
    description:
      "Verify every salary figure and date before signing or sharing the document.",
  },
];
const salaryIncrementContent = [
  {
    icon: FaUser,
    title: "Employee Information",
    description:
      "The employee's name, ID, designation, department and work location.",
  },
  {
    icon: FaClipboardList,
    title: "Increment Confirmation",
    description:
      "A formal statement confirming the employee's salary increase.",
  },
  {
    icon: FaClock,
    title: "Appraisal Period",
    description:
      "The period during which the employee's performance was reviewed.",
  },
  {
    icon: FaCoins,
    title: "Current and Revised Salary",
    description:
      "The previous and updated annual CTC, monthly salary and variable compensation.",
  },
  {
    icon: FaPercentage,
    title: "Increment Percentage",
    description:
      "The percentage or amount by which the employee's salary has increased.",
  },
  {
    icon: FaCalendar,
    title: "Effective Date",
    description:
      "The date from which the revised compensation becomes applicable.",
  },
  {
    icon: FaTable,
    title: "Performance Appreciation",
    description:
      "A professional message recognising the employee's contribution and achievements.",
  },
  {
    icon: FaImage,
    title: "Salary Annexure",
    description:
      "A detailed breakdown of revised salary components, allowances and employer contributions.",
  },
];

const features = [
  <>
    <strong>HR Professionals:</strong> Create clear and professional salary
    increment letters for employees after performance reviews.
  </>,
  <>
    <strong>Business Owners:</strong> Formally communicate salary increases to
    employees in small and growing businesses.
  </>,
  <>
    <strong>Department Managers:</strong> Recommend and document salary
    revisions for deserving team members.
  </>,
  <>
    <strong>Startups and SMEs:</strong> Use a ready-made increment letter format
    without creating documents from scratch.
  </>,
  <>
    <strong>Large Organisations:</strong> Maintain consistent salary revision
    communication across departments and locations.
  </>,
  <>
    <strong>HR Consultants:</strong> Prepare customised increment letters for
    clients and their employees.
  </>,
];

const Faqs1 = [
  {
    title: "What Is a Salary Increment Letter?",
    content:
      "A salary increment letter is an official document used to communicate an employee's revised salary, increment amount, effective date, and other relevant pay details.",
  },
  {
    title: "What Details Are Included in an Increment Letter?",
    content:
      "A salary increment letter may include employee details, previous salary, revised salary, increment amount or percentage, effective date, appraisal details, and authorised signatory information.",
  },
  {
    title: "Should the Previous Salary Be Mentioned in an Increment Letter?",
    content:
      "Yes. Mentioning the previous and revised salary helps clearly show the salary change and gives the employee a clear record of the increment.",
  },
  {
    title: "Can an Increment Letter Include the Salary Increment Percentage?",
    content:
      "Yes. The increment percentage or amount can be included to clearly communicate the salary increase along with the revised compensation.",
  },
  {
    title: "Can Variable Pay Be Included in a Salary Increment Letter?",
    content:
      "Yes. If the salary revision includes changes to variable pay, bonus, or other compensation components, these details can be added to the letter.",
  },
  {
    title: "Who Can Issue a Salary Increment Letter?",
    content:
      "A salary increment letter is generally issued by the HR team or an authorised manager, department head, or company representative.",
  },
];
const schema = [
  // =========================================================
  // SCHEMA 1: WebApplication
  // =========================================================
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Waggex Free Salary Increment Letter Generator",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description:
      "Free online salary increment letter generator. Enter company details, employee information, current salary, revised salary, and appraisal details to create and download a professional increment letter PDF instantly.",
    url: "https://www.waggex.com/hr-documents/salaryincrement-letter-generator",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
    },
    featureList: [
      "Free salary increment letter generator India",
      "Online increment letter format India",
      "Increment letter PDF download",
      "Employee salary hike letter template India",
      "Professional appraisal increment letter maker India",
    ],
  },

  // =========================================================
  // SCHEMA 2: FAQPage
  // =========================================================
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is a salary increment letter format?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "A Salary Increment Letter Format is a standard structure containing employee details, current salary, revised salary, increment amount or percentage, effective date, and approval information.",
        },
      },
      {
        "@type": "Question",
        name: "Should the previous salary be mentioned in an increment letter?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Including the current salary alongside the revised salary makes the increment clear and provides a clear record of the salary change.",
        },
      },
      {
        "@type": "Question",
        name: "Should the increment percentage be included?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes, where applicable. Including the increment percentage helps clearly communicate the scale of the salary increase along with the revised compensation.",
        },
      },
      {
        "@type": "Question",
        name: "Can the increment letter be issued digitally?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. A salary increment letter can be generated as a PDF and shared digitally with the employee through email or an HR portal for employee and payroll records.",
        },
      },
    ],
  },

  // =========================================================
  // SCHEMA 3: Organization
  // =========================================================
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Waggex",
    url: "https://www.waggex.com/",
    logo: "https://www.waggex.com/waggex-payroll-software-logo.png",
    description:
      "Waggex provides free HR document generation tools and HRMS software for Indian businesses.",
    areaServed: "IN",
  },
];
const relievingLetterPoints = [
  "Employee Information",
  "Appraisal Period",
  "Current Salary Details",
  "Revised Salary and Increment",
  "Effective Date and Recognition",
  "Authorised Approval",
];

const experiencePoints = [
  "Salary increase",
  "Change in fixed & variable pay",
  "Revised allowances",
  "Market alignment",
  "Role-based compensation",
];

const relievingPoints = [
  "Annual appraisal",
  "Employee performance",
  "Retention",
  "Market correction",
  "Additional responsibilities",
];
export default function SalaryIncrementLetterGenerator() {
  return (
    <>
      <Helmet>
        <title>Generate Salary Increment Letter Online | Free Download</title>
        <meta
          name="description"
          content="Create and download professional salary increment letters online for free. Enter salary and appraisal details, generate, and download your PDF instantly"
        />
        <meta
          name="keywords"
          content="salary increment letter generator online,salary increment letter format India,free increment letter maker online India,salary hike letter template India,increment letter PDF generator India,employee salary increment letter India,appraisal increment letter format India,salary increment letter Word format India,pay revision letter India"
        />
        <link
          rel="canonical"
          href="https://www.waggex.com/hr-documents/salaryincrement-letter-generator"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema),
          }}
        />
      </Helmet>
      <div className="px-2 py-3 sm:px-4">
        <section className="rounded-xl border border-white px-2 sm:px-4 py-6 shadow-sm backdrop-blur bg-[#ffffff40]">
          <div className="text-center mb-10">
            <h1 className="text-[#10324F] text-lg sm:text-3xl font-roboto font-bold leading-snug mb-4">
              Free Salary Increment Letter Generator Online
            </h1>

            <p className="mx-auto mt-8 max-w-5xl text-sm sm:text-lg leading-6 text-[#10324F] min-h-18 sm:min-h-12">
              Create a professional salary increment letter online with revised
              pay, appraisal details, effective date, and other changes.
              Generate and download the PDF in minutes
            </p>

            <div className="mt-10 flex flex-col justify-center gap-3 sm:flex-row mb-10">
              <button
                onClick={() =>
                  (window.location.href = "https://app.waggex.com/signup")
                }
                className="w-full sm:w-auto bg-[#10324F] hover:bg-[#10324F] transition-all duration-300 text-white px-14 py-3 rounded-xl shadow-md text-sm sm:text-base font-medium"
              >
                Start Free Trial
              </button>

              <button
                onClick={() => (window.location.href = "/contact-us")}
                className="w-full sm:w-auto border border-[#10324F] text-[#10324F] hover:bg-[#10324F] hover:text-white transition-all duration-300 px-14 py-3 rounded-xl text-sm sm:text-base font-medium"
              >
                Book a Demo
              </button>
            </div>
          </div>
          <SalaryIncrementLetterGeneratorForm />
          <section className="w-full px-4 py-8 sm:px-6 md:px-8  mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 ">
                {/* Left Content */}
                <div>
                  <div className="mt-4">
                    <div className="mb-6 flex items-center gap-3">
                      <span className="w-15.5 h-0.5 rounded bg-linear-to-r from-[#10324F] via-[#607D8B] to-transparent shadow-[0_0_6px_rgba(16,50,79,0.15)]"></span>
                      <span className="text-sm font-medium uppercase tracking-wider text-[#10324F]">
                        WHAT IS AN INCREMENT LETTER?
                      </span>
                    </div>

                    <h2 className="text-xl md:text-3xl font-semibold text-[#10324F] mt-3 mb-5">
                      Its An Official Salary Increment Documentation
                    </h2>

                    <p className="text-[#10324F] mb-6 leading-relaxed max-w-2xl">
                      A salary increment letter formally communicates a change
                      in an employee’s compensation. It records the revised
                      salary, effective date, and appraisal outcome, giving both
                      the employee and employer a clear reference for the
                      updated pay terms
                    </p>
                  </div>
                </div>

                {/* Right Content */}
                <div className="flex flex-col">
                  {relievingLetterPoints.map((item, index) => (
                    <React.Fragment key={item}>
                      <div className="relative z-10 flex min-h-12.5 items-center justify-center rounded-lg border border-white  bg-linear-to-b from-white to-[#D1DFEA]/80  hover:bg-white/90 px-4 py-3 text-center text-sm font-medium text-[#10324F] shadow-sm backdrop-blur-sm transition sm:min-h-12.5 sm:text-base">
                        {item}
                      </div>

                      {index < relievingLetterPoints.length - 1 && (
                        <div className="mx-auto h-6 border-l border-dashed border-[#10324F]/80" />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </section>
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              {/* Heading */}
              <div className="text-center max-w-5xl mx-auto">
                <p className="text-sm font-medium uppercase text-[#10324F] sm:text-base">
                  WHAT DOES THE SALARY INCREMENT LETTER FORMAT INCLUDE?
                </p>
                <h2 className="mt-4 text-xl leading-7.5 sm:text-3xl font-roboto font-bold text-[#16324F] uppercase">
                  Key Details Covered In A Salary Increment Letter
                </h2>

                <p className="mt-6 text-sm leading-6 sm:text-base text-[#10324F] px-2 font-roboto  text-center max-w-3xl mx-auto">
                  Build a complete salary increment letter with confidence and
                  essential information organized clearly. Include employee
                  details, revised compensation, increment amount, effective
                  date, and appraisal details
                </p>
              </div>

              {/* Cards */}
              <div className="mt-8 sm:mt-12 grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
                {salaryIncrementContent.map((item, index) => {
                  const Icon = item.icon;

                  return (
                    <div
                      key={index}
                      className="group flex min-h-25.5 items-start gap-4 rounded-xl border border-white/90 bg-linear-to-b from-white/60 to-[#D1DFEA]/70  hover:bg-white/90 px-5 py-5 transition-all duration-200 shadow-sm"
                    >
                      {/* Icon */}
                      <div className="flex shrink-0 items-center justify-center pt-1">
                        <Icon className="text-2xl text-[#10324F]" />
                      </div>

                      {/* Content */}
                      <div>
                        <h3 className="text-sm sm:text-lg font-semibold leading-5 text-[#10324F]">
                          {item.title}
                        </h3>

                        <p className="mt-2 text-xs sm:text-sm leading-5 text-[#10324F]">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              {/* Heading */}
              <div className="text-center">
                <p className="text-sm font-medium uppercase text-[#10324F] sm:text-base">
                  HOW TO CREATE AN INCREMENT LETTER ONLINE
                </p>

                <h2 className="mt-4 text-xl font-bold leading-tight text-[#10324F] sm:text-3xl">
                  Create Your Salary Increment Letter & Download Instantly No
                  Signup
                </h2>
              </div>

              {/* Steps */}

              {/* ROW 1 */}
              <div className=" mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                {steps.slice(0, 4).map((step, index) => (
                  <div key={step.number} className="relative">
                    {/* LEFT LINE */}
                    <div className="absolute left-0 right-1/2 top-6.5  border-t border-dashed border-[#10324F] hidden lg:block" />

                    {/* RIGHT LINE + ARROW */}
                    {index < 3 && (
                      <div className="absolute left-1/2 right-0 top-6.5  border-t border-dashed border-[#10324F] hidden lg:block">
                        <span className="absolute -right-0.5 -top-3.75 text-lg text-[#10324F]">
                          ▶
                        </span>
                      </div>
                    )}

                    {/* STEP BUTTON */}
                    <div className="relative z-10 mx-auto flex h-13 w-32.5 items-center justify-center rounded-xl bg-[#10324F] text-lg font-medium text-white">
                      Step {step.number}
                    </div>

                    {/* CONTENT */}
                    <div className="mx-auto mt-5 max-w-70 px-2 text-center mb-8 sm:mb-4">
                      <h3 className="text-lg font-bold leading-6 text-[#10324F]">
                        {step.title}
                      </h3>

                      <p className="mt-2 text-sm leading-5 text-[#1324F]">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* ROW 2 */}
              <div className=" mt-4 lg:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                {steps.slice(4, 8).map((step, index) => (
                  <div key={step.number} className="relative">
                    {/* LEFT LINE — IMPORTANT FOR STEP 5 */}
                    <div className="absolute left-0 right-1/2 top-6.5 hidden border-t border-dashed border-[#10324F] lg:block" />

                    {/* RIGHT LINE + ARROW */}
                    {index < 3 && (
                      <div className="absolute left-1/2 right-0 top-6.5 hidden border-t border-dashed border-[#10324F] lg:block">
                        <span className="absolute -right-0.5 -top-3.75 text-lg text-[#10324F]">
                          ▶
                        </span>
                      </div>
                    )}

                    {/* STEP BUTTON */}
                    <div className="relative z-10 mx-auto flex h-13 w-32.5 items-center justify-center rounded-xl bg-[#163957] text-lg font-medium text-white">
                      Step {step.number}
                    </div>

                    {/* CONTENT */}
                    <div className="mx-auto mt-5 max-w-70 px-2 text-center mb-8 sm:mb-4">
                      <h3 className="text-lg font-bold leading-6 text-[#10324F]">
                        {step.title}
                      </h3>

                      <p className="mt-2 text-sm leading-5 text-[#10324F]">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
          <section className="w-full px-4 py-8 sm:px-8 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-2 lg:gap-8">
                {/* LEFT CONTENT */}
                <div>
                  {/* Small heading */}
                  <div className="mb-6 flex items-center gap-3">
                    <span className="w-15.5 h-0.5 rounded bg-linear-to-r from-[#10324F] via-[#607D8B] to-transparent shadow-[0_0_6px_rgba(16,50,79,0.15)]"></span>
                    <span className="text-sm font-medium uppercase tracking-wider text-[#10324F]">
                      UNDERSTANDING THE DIFFERENCE
                    </span>
                  </div>
                  {/* Main heading */}
                  <h2 className="text-xl font-semibold leading-tight text-[#10324F] sm:text-3xl">
                    Increment Letter vs Salary Revision Letter
                  </h2>
                  {/* Intro */}
                  <p className="mt-4 mb-2 max-w-2xl text-sm leading-8 text-[#10324F]">
                    Use an Increment Letter or Salary Revision Letter to
                    formally share updated pay details with an employee. The
                    right format can depend on your company’s HR process and how
                    the salary change is communicated
                  </p>{" "}
                  <strong>CONTEXT POINT CAN BOTH BE USED? </strong>{" "}
                  <p className="mt-2 max-w-2xl text-sm leading-8 text-[#10324F]">
                    Yes. Both terms are commonly used by organisations to
                    communicate an increase in an employee’s salary
                  </p>
                </div>

                {/* RIGHT COMPARISON */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {/* ================= OFFER LETTER ================= */}
                  <div className="flex flex-col gap-3">
                    {/* HEADER CARD */}
                    <div className="flex min-h-20.5 items-center gap-5 rounded-xl border border-white bg-linear-to-b from-white/60 to-[#D1DFEA]/30 px-4 py-4 shadow-sm transition-all duration-200 hover:bg-white/30">
                      <FaEnvelopeOpenText className="shrink-0 text-[34px] text-[#10324F]" />

                      <h3 className="text-sm font-semibold text-[#10324F]">
                        Increment Letter
                      </h3>
                    </div>

                    {/* LIST CARD */}
                    <div className="min-h-73 rounded-xl border border-white bg-linear-to-b  from-white/60 to-[#D1DFEA]/30 px-4 py-6 shadow-sm transition-all duration-200 hover:bg-white/30">
                      <div className="space-y-5">
                        {relievingPoints.map((point) => (
                          <div key={point} className="flex items-start gap-4">
                            <FaCaretRight className="mt-1 shrink-0 text-sm text-[#10324F]" />

                            <span className="text-sm leading-6 text-[#10324F]">
                              {point}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* ================= APPOINTMENT LETTER ================= */}
                  <div className="flex flex-col gap-3">
                    {/* HEADER CARD */}
                    <div className="flex min-h-20.5 items-center gap-5 rounded-xl border border-white bg-linear-to-b from-white/60 to-[#D1DFEA]/30 px-4 py-4 shadow-sm transition-all duration-200 hover:bg-white/30">
                      <FaEnvelope className="shrink-0 text-[34px] text-[#10324F]" />

                      <h3 className="text-sm font-semibold text-[#10324F]">
                        Salary Revision Letter
                      </h3>
                    </div>

                    {/* LIST CARD */}
                    <div className="min-h-73 rounded-xl border border-white bg-linear-to-b from-white/60 to-[#D1DFEA]/30 px-2 py-6 shadow-sm transition-all duration-200 hover:bg-white/30">
                      <div className="space-y-5">
                        {experiencePoints.map((point) => (
                          <div key={point} className="flex items-start gap-4">
                            <FaCaretRight className="mt-1 shrink-0 text-sm text-[#10324F]" />

                            <span className="text-sm leading-6 text-[#10324F]">
                              {point}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              {/* Heading */}
              <div className="text-center max-w-5xl mx-auto">
                <p className="text-sm font-normal text-[#10324F] uppercase mb-4">
                  Why Use an Online Increment Letter Format?
                </p>
                <h2 className="text-xl leading-7.5 sm:text-3xl font-roboto font-bold text-[#16324F]  uppercase">
                  Create Salary Revision Documents Faster
                </h2>
              </div>
              <div className="mt-8 sm:mt-12 grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-3 gap-4">
                {companystepsFeatures.map((item) => {
                  // const Icon = item.icon;

                  return (
                    <div
                      key={item.id}
                      className="rounded-xl border border-white/90  bg-linear-to-b from-white/60 to-[#D1DFEA]/60  hover:bg-white/30 p-4 duration-200 shadow-sm transition-all"
                    >
                      {/* Top Row */}
                      <div className="flex items-center justify-center mb-5">
                        {/* <div className="w-14 h-10 rounded-md bg-[#16324F] font-roboto text-white flex items-center justify-center text-lg font-bold shadow">
                          {item.id}
                        </div> */}
                        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#10324F] text-xl text-white">
                          {item.icon}
                        </div>
                      </div>

                      {/* Content */}
                      <h2 className="text-center text-sm sm:text-base font-semibold text-[#10324F] font-roboto">
                        {item.title}
                      </h2>

                      <p className="mt-2 text-center text-sm leading-6 text-[#10324F] font-roboto">
                        {item.desc}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              {/* Heading */}
              <div className="text-center max-w-5xl mx-auto mb-4">
                <p className="text-sm font-normal text-[#10324F] uppercase mb-4">
                  WHO CAN USE THIS INCREMENT LETTER FORMAT?
                </p>
                <h2 className="font-roboto text-center text-[#10324F] text-xl md:text-3xl font-bold uppercase tracking-wide">
                  Suitable for Every Company
                </h2>
              </div>

              {/* Features Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6 place-items-start">
                {features.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 text-[#10324F] px-2 py-3  rounded-2xl"
                  >
                    {/* Check Icon */}
                    <div className="mt-1 flex items-center justify-center text-[#10324F]">
                      <Check size={30} aria-hidden="true" />
                    </div>

                    {/* Text */}
                    <p className="font-roboto text-sm sm:text-lg font-medium leading-relaxed">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
          <FAQ
            title="Frequently Asked Question"
            items={Faqs1}
            defaultOpenIndex={0}
          />
          <section className="w-full bg-[#F4F6F8]/60 px-6 py-10 rounded-xl border border-white mt-8">
            <div className="max-w-6xl mx-auto text-center mt-4 mb-4">
              <h2 className="text-[#10324F] text-xl sm:text-3xl font-roboto font-bold uppercase">
                Create Your Salary Increment Letter Online
              </h2>

              <p className="mt-4 text-[#10324F] text-sm sm:text-base">
                Make a professional salary increment letter with revised pay,
                appraisal details, and the effective date. No Signup Needed
                ready-to-use Instantly Download PDF for your employee
              </p>

              <div className="mt-8 flex flex-col sm:flex-row justify-center items-center gap-4">
                <button
                  className="
              w-full sm:w-auto
              min-w-55
              px-8 py-3
              rounded-xl
              bg-[#10324F]
              text-white text-xs sm:text-sm
              font-medium
              shadow-md
              transition-all duration-300
              hover:scale-105
            "
                  onClick={() => {
                    window.scrollTo({
                      top: 0,
                      behavior: "smooth",
                    });
                  }}
                >
                  Generate Salary Increment Letter
                </button>

                <button
                  className="
              w-full sm:w-auto
              min-w-55
              px-8 py-3 text-xs sm:text-sm
              rounded-xl
              border border-[#10324F]
              text-[#10324F]
              font-medium
              transition-all duration-300
              hover:bg-white
              hover:text-[#16385C]
            "
                  onClick={() => (window.location.href = "/all-hr-documents")}
                >
                  Explore more
                </button>
              </div>
            </div>
          </section>
        </section>
      </div>
    </>
  );
}
